import React, { useState, useEffect } from 'react';
import { Button, Form, Col, Row, Modal } from 'react-bootstrap';
import SpecialSelect from '../ui/SpecialSelect';
import { formatMoney } from '../../utils/Functions';
import { UnitType, ProjectType } from '../../interfaces/project';

export const Simulator = ({ project }: { project: ProjectType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<UnitType | null>(null);
  const [creditPercentage, setCreditPercentage] = useState<string>('');
  const [interestRate, setInterestRate] = useState(4.5);
  const [termYears, setTermYears] = useState<string>('');
  const [calculatedValues, setCalculatedValues] = useState({
    creditAmount: 0,
    downPayment: 0,
    estimatedDividendUF: 0,
    estimatedDividendCLP: 0,
    requiredIncome: 0,
    clientDownPayment: 0,
  });
  const [creditError, setCreditError] = useState('');
  const [ufValue, setUfValue] = useState<number>(0);

  useEffect(() => {
    // Fetch UF value from API
    fetch('https://mindicador.cl/api/uf')
      .then(response => response.json())
      .then(data => {
        const latestUfValue = data.serie[0].valor;
        setUfValue(latestUfValue);
      })
      .catch(error => console.error('Error fetching UF value:', error));
  }, []);

  const handleOpenSimulator = () => setIsOpen(true);
  const handleCloseSimulator = () => setIsOpen(false);

  const unitsOptions = project.units.map((unit: UnitType) => ({
    value: unit.id,
    label: `${unit.number} - ${unit.currency} ${formatMoney(unit.list_price)}`,
  }));

  useEffect(() => {
    if (selectedUnit && creditPercentage && termYears && ufValue) {
      const unitPrice = selectedUnit.list_price;
      const creditAmount = (Number(creditPercentage) / 100) * unitPrice;
      const downPayment = unitPrice - creditAmount;
      const interestRateDecimal = interestRate / 100;
      const monthlyInterestRate = interestRateDecimal / 12;
      const numberOfPayments = Number(termYears) * 12;
      const estimatedDividendUF = Math.abs(PMT(monthlyInterestRate, numberOfPayments, creditAmount));
      
      const estimatedDividendCLP = Math.round(estimatedDividendUF * ufValue);
      const requiredIncome = Math.round(estimatedDividendCLP * 4);
      const pieBonus = project.pie_bonus_conditions ? parseFloat(project.pie_bonus_conditions) / 100 : 0;
      const clientDownPayment = downPayment - (unitPrice * pieBonus);

      setCalculatedValues({
        creditAmount,
        downPayment,
        estimatedDividendUF,
        estimatedDividendCLP,
        requiredIncome,
        clientDownPayment,
      });
    }
  }, [selectedUnit, creditPercentage, termYears, ufValue]);

  const PMT = (rate: number, nper: number, pv: number): number => {
    if (rate === 0) return -(pv / nper);
    const pvif = Math.pow(1 + rate, nper);
    return -(rate * pv * pvif) / (pvif - 1);
  };

  // Function to format numbers with thousands separator
  const formatNumber = (num: number) => {
    return num.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleCreditPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (Number(value) > 99) {
      setCreditError('El porcentaje de crédito no puede ser mayor a 99.');
    } else {
      setCreditError('');
      setCreditPercentage(value);
    }
  };

  const handleTermYearsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setTermYears(value);
  };

  return (
    <div>
      <Button onClick={handleOpenSimulator} variant="link" style={{ color: 'inherit', fontWeight: 'normal', textDecoration: 'none', padding: '0', margin: '0 10px' }}>
        Simular 
      </Button>

      <Modal show={isOpen} onHide={handleCloseSimulator} centered dialogClassName="custom-modal-width">
        <Modal.Header closeButton>
          <Modal.Title style={{ color: '#FF6600' }}>Simulador</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <Col>
                <Form.Group controlId="unitSelect">
                  <Form.Label>Unidad</Form.Label>
                  <SpecialSelect
                    options={unitsOptions}
                    onChange={(option: { value: number; label: string }) => {
                      const unit = project.units.find(u => u.id === option.value);
                      setSelectedUnit(unit || null);
                    }}
                    selected={selectedUnit ? { value: selectedUnit.id, label: `${selectedUnit.number} - ${selectedUnit.currency} ${formatMoney(selectedUnit.list_price)}` } : null}
                    placeholder="Seleccione una unidad..."
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group controlId="creditPercentage">
                  <Form.Label>Porcentaje de Crédito</Form.Label>
                  <Form.Control
                    type="text"
                    value={creditPercentage}
                    onChange={handleCreditPercentageChange}
                    placeholder="Ingrese porcentaje"
                  />
                  {creditError && <small style={{ color: 'red' }}>{creditError}</small>}
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="termYears">
                  <Form.Label>Plazo (años)</Form.Label>
                  <Form.Control
                    type="text"
                    value={termYears}
                    onChange={handleTermYearsChange}
                    placeholder="Ingrese plazo"
                  />
                </Form.Group>
              </Col>
            </Row>
            <hr style={{ borderColor: '#FF6600' }} />
            <Row>
              <Col>
                <h5 style={{ color: '#FF6600' }}>Crédito</h5>
                <p><strong>Crédito a Solicitar:</strong> {formatNumber(calculatedValues.creditAmount)} UF</p>
                <p><strong>Dividendo Estimado en UF:</strong> {formatNumber(calculatedValues.estimatedDividendUF)} UF</p>
                <p><strong>Dividendo Estimado $:</strong> {formatNumber(calculatedValues.estimatedDividendCLP)} CLP</p>
                <p><strong>Renta Requerida (25%):</strong> {formatNumber(calculatedValues.requiredIncome)} CLP</p>
              </Col>
              <Col>
                <h5 style={{ color: '#FF6600' }}>Pie</h5>
                <p><strong>Pie a Enterar:</strong> {formatNumber(calculatedValues.downPayment)} UF</p>
                <p><strong>Pago Pie del Cliente:</strong> {formatNumber(calculatedValues.clientDownPayment)} UF</p>
              </Col>
            </Row>
            <div style={{ textAlign: 'right', marginTop: '20px' }}>
              <strong>Sujeto a UF del día: {formatNumber(ufValue)}</strong>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <style>
        {`
          .custom-modal-width {
            max-width: 800px;
          }
        `}
      </style>
    </div>
  );
}; 